import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../App';
import { UserTaskState, ChecklistTask, UserProfile, ChecklistPhase } from '../types';
import { appStorage } from '../utils/storage';
import { safeJsonParse, validateTaskStates } from '../utils/validation';
import {
  CheckSquare, Square, Trophy, Check,
  Globe, MessageSquare, ChevronDown, ChevronUp,
  Clock, AlertTriangle, PlayCircle, Users, Flame
} from 'lucide-react';

interface ChecklistProps {
  userProfile: UserProfile;
}

const DEPENDENCIES: Record<string, string> = {
  'doc_1': '⚠️ Critical: Complete OCI before booking non-refundable flights',
  'res_1': '⚠️ Tip: Research neighborhoods before finalizing school choices',
  'sch_1': '⚠️ Pre-requisite: Secure school admission before signing long-term lease',
  'hou_3': '⚠️ Important: Decide Rent vs Buy before shipping furniture',
  'imm_1': '⚠️ Blocker: Aadhaar is needed for almost all local services'
};

const CATEGORY_GROUPS: Record<string, string[]> = {
  'Financial': ['Financial Planning', 'Financial', 'Exit Tasks'],
  'Documentation': ['Documentation', 'India Preparation', 'Immediate Tasks'],
  'Housing': ['Research', 'Housing', 'Household Setup', 'Lifestyle'],
  'Schools': ['Schools', 'Kids Settlement'],
  'Shipping': ['Shipping']
};

const Checklist: React.FC<ChecklistProps> = ({ userProfile }) => {
  const { checklist } = useData();
  // Determine initial country based on profile or default to US
  const initialCountry = userProfile?.sourceCountry === 'UK' ? 'UK' 
    : userProfile?.sourceCountry === 'Germany' ? 'Germany' 
    : 'US';

  const [selectedCountry, setSelectedCountry] = useState<string>(initialCountry);
  const [expandedPhase, setExpandedPhase] = useState<string | null>(null);
  const [expandedTaskIds, setExpandedTaskIds] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  
  // Persistent Task State with validation
  const [userTasks, setUserTasks] = useState<UserTaskState[]>(() => {
    const saved = appStorage.getItem('puneRelocationProgress_v2');
    const parsed = safeJsonParse<unknown[]>(saved);
    return validateTaskStates(parsed);
  });

  // Initialize tasks on load
  useEffect(() => {
    if (userTasks.length === 0) {
      const allTasks: UserTaskState[] = [];
      checklist.phases.forEach(phase => {
        phase.categories.forEach(cat => {
          cat.tasks.forEach(task => {
            allTasks.push({ id: task.id, completed: false, included: true });
          });
        });
      });
      setUserTasks(allTasks);
    }
  }, [checklist]);

  // Save to persistent storage whenever tasks change
  useEffect(() => {
    if (userTasks.length > 0) {
      appStorage.setItem('puneRelocationProgress_v2', JSON.stringify(userTasks));
    }
  }, [userTasks]);

  // Determine Active Phase based on Timeline
  const timelineInfo = useMemo(() => {
    const timeline = userProfile?.timeline || 'exploring';
    let activePhaseId = 'phase_1';
    let catchUpPhases: string[] = [];

    if (timeline === 'less_than_6_months') {
      activePhaseId = 'phase_3'; // Pre-Move Execution
      catchUpPhases = ['phase_1', 'phase_2'];
    } else if (timeline === '6_to_12_months') {
      activePhaseId = 'phase_2'; // Active Planning
      catchUpPhases = ['phase_1'];
    } else if (timeline === '1_to_2_years') {
      activePhaseId = 'phase_1';
    }

    return { activePhaseId, catchUpPhases };
  }, [userProfile]);

  // Set initial expanded phase based on timeline (run only once)
  useEffect(() => {
    if (!expandedPhase) {
      setExpandedPhase(timelineInfo.activePhaseId);
    }
  }, [timelineInfo.activePhaseId]);

  // --- LOGIC: URGENT ITEMS ---
  const isTaskRelevant = (task: ChecklistTask) => {
    return task.sourceCountry.includes(selectedCountry) || task.sourceCountry.length === 0;
  };

  const urgentTasks = useMemo(() => {
    const timeline = userProfile?.timeline || 'exploring';
    if (timeline === 'exploring' || timeline === '1_to_2_years') return [];

    let urgentIds: string[] = [];
    
    // Collect all incomplete tasks first
    const incompleteTaskIds = userTasks.filter(t => !t.completed).map(t => t.id);

    checklist.phases.forEach(phase => {
      phase.categories.forEach(cat => {
        cat.tasks.forEach(task => {
           if (!isTaskRelevant(task)) return;
           if (!incompleteTaskIds.includes(task.id)) return;

           // Logic for "Less than 6 months"
           if (timeline === 'less_than_6_months') {
             // All incomplete tasks from Phase 1 & 2 are urgent catch-up
             if (phase.id === 'phase_1' || phase.id === 'phase_2') {
               urgentIds.push(task.id);
             }
             // High priority items from Phase 3 are urgent
             if (phase.id === 'phase_3' && task.priority === 'high') {
               urgentIds.push(task.id);
             }
           }
           
           // Logic for "6 to 12 months"
           if (timeline === '6_to_12_months') {
             // Phase 1 catch up
             if (phase.id === 'phase_1') {
               urgentIds.push(task.id);
             }
             // Phase 2 High Priority
             if (phase.id === 'phase_2' && task.priority === 'high') {
               urgentIds.push(task.id);
             }
           }
        });
      });
    });

    // Flatten to Task objects for display
    const result: { phaseName: string; task: ChecklistTask }[] = [];
    checklist.phases.forEach(phase => {
      phase.categories.forEach(cat => {
        cat.tasks.forEach(task => {
          if (urgentIds.includes(task.id)) {
            result.push({ phaseName: phase.name, task });
          }
        });
      });
    });

    return result;
  }, [userTasks, userProfile, selectedCountry, checklist]);

  // --- HANDLERS ---

  const toggleTaskCompletion = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setUserTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
  };

  const toggleTaskDetails = (id: string) => {
    setExpandedTaskIds(prev => 
      prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id]
    );
  };

  const togglePhase = (id: string) => {
    setExpandedPhase(prev => prev === id ? null : id);
  };

  // --- FILTERS & CALCULATIONS ---

  const getPhaseProgress = (phase: ChecklistPhase) => {
    let total = 0;
    let completed = 0;
    
    phase.categories.forEach(cat => {
      cat.tasks.forEach(task => {
        if (isTaskRelevant(task)) {
          total++;
          const state = userTasks.find(t => t.id === task.id);
          if (state?.completed) completed++;
        }
      });
    });

    return total === 0 ? 0 : Math.round((completed / total) * 100);
  };

  const overallProgress = useMemo(() => {
    let total = 0;
    let completed = 0;

    checklist.phases.forEach(phase => {
      phase.categories.forEach(cat => {
        cat.tasks.forEach(task => {
          if (isTaskRelevant(task)) {
            total++;
            const state = userTasks.find(t => t.id === task.id);
            if (state?.completed) completed++;
          }
        });
      });
    });

    return total === 0 ? 0 : Math.round((completed / total) * 100);
  }, [userTasks, selectedCountry, checklist]);

  // --- RENDER HELPERS ---

  const getPriorityBadge = (p: string) => {
    switch(p) {
      case 'high': return <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded uppercase">High</span>;
      case 'medium': return <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded uppercase">Medium</span>;
      case 'low': return <span className="text-[10px] font-bold text-slate-500 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded uppercase">Low</span>;
      default: return null;
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto pb-24 md:pb-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            Relocation Checklist <Trophy className="text-yellow-500" size={24} />
            <span className="text-xs font-medium px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">🇮🇳 All NRIs</span>
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Personalized for moving from <span className="font-semibold text-slate-700">{selectedCountry}</span> • {timelineInfo.catchUpPhases.length > 0 ? 'Expedited Timeline' : 'Standard Timeline'}
          </p>
        </div>

        <div className="flex gap-2">
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm">
            <Globe size={16} className="text-slate-400" />
            <select 
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="bg-transparent font-bold text-indigo-600 focus:outline-none text-sm cursor-pointer"
            >
              <option value="US">USA 🇺🇸</option>
              <option value="UK">UK 🇬🇧</option>
              <option value="Germany">Germany 🇩🇪</option>
            </select>
          </div>
        </div>
      </div>

      {/* URGENT ITEMS SECTION */}
      {urgentTasks.length > 0 && overallProgress < 100 && (
        <div className="bg-orange-50 rounded-xl border border-orange-200 p-4 mb-8 shadow-sm animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="text-orange-500 fill-orange-500" size={20} />
            <h3 className="font-bold text-orange-800">Urgent Actions Required</h3>
            <span className="bg-orange-200 text-orange-800 text-xs font-bold px-2 py-0.5 rounded-full">
              {urgentTasks.length} items
            </span>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
            {urgentTasks.map(({ task, phaseName }) => (
              <div key={`urgent-${task.id}`} className="bg-white p-3 rounded-lg border border-orange-100 flex items-start gap-3 shadow-sm hover:shadow-md transition">
                 <button
                    onClick={(e) => toggleTaskCompletion(task.id, e)}
                    className="mt-0.5 text-slate-300 hover:text-orange-500 transition-colors"
                    aria-label={`Mark ${task.task} as complete`}
                 >
                   <Square size={20} />
                 </button>
                 <div>
                   <p className="text-sm font-semibold text-slate-800">{task.task}</p>
                   <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-medium">{phaseName}</span>
                      {DEPENDENCIES[task.id] && (
                        <span className="text-[10px] text-orange-600 font-bold flex items-center gap-1">
                          <AlertTriangle size={10} /> Dependency Warning
                        </span>
                      )}
                   </div>
                 </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overall Progress */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex items-center gap-4 sticky top-0 z-30 md:relative">
         <div className="relative h-14 w-14 flex items-center justify-center flex-shrink-0">
            <svg className="h-full w-full transform -rotate-90" viewBox="0 0 36 36">
              <path className="text-slate-100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3"/>
              <path className="text-indigo-600 transition-all duration-1000 ease-out" strokeDasharray={`${overallProgress}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="3" />
            </svg>
            <span className="absolute text-xs font-bold text-slate-700">{overallProgress}%</span>
         </div>
         <div className="flex-1 min-w-0">
           <h3 className="font-bold text-slate-800 truncate">Your Move Progress</h3>
           <p className="text-xs text-slate-500 truncate">
             {overallProgress < 20 ? "Just getting started! Follow the phases below." 
               : overallProgress < 50 ? "Making good progress. Keep checking off high priority items." 
               : overallProgress < 80 ? "Almost there! Focus on the final execution steps." 
               : "You're ready to fly! Safe travels."}
           </p>
         </div>
      </div>

      {/* Category Filters */}
      <div className="mb-6 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex gap-2">
          {['All', ...Object.keys(CATEGORY_GROUPS)].map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition border
                ${activeCategory === cat 
                  ? 'bg-slate-800 text-white border-slate-800' 
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Phases Accordion */}
      <div className="space-y-4">
        {checklist.phases.map((phase) => {
          const progress = getPhaseProgress(phase);
          const isExpanded = expandedPhase === phase.id;
          const isActive = timelineInfo.activePhaseId === phase.id;
          const isCatchUp = timelineInfo.catchUpPhases.includes(phase.id);
          const isComplete = progress === 100;

          // Filter Categories in this Phase
          const visibleCategories = phase.categories.filter(cat => {
             if (activeCategory === 'All') return true;
             const mappedCategories = CATEGORY_GROUPS[activeCategory] || [];
             return mappedCategories.includes(cat.name);
          });
          
          // Check if phase has relevant tasks after country filtering
          const hasRelevantTasks = visibleCategories.some(cat => 
            cat.tasks.some(isTaskRelevant)
          );

          if (!hasRelevantTasks) return null;

          return (
            <div 
              key={phase.id} 
              className={`bg-white rounded-xl border transition-all duration-300 overflow-hidden
                ${isExpanded ? 'shadow-md border-indigo-200 ring-1 ring-indigo-50' : 'shadow-sm border-slate-200'}
                ${isActive && !isExpanded ? 'border-l-4 border-l-indigo-500' : ''}
              `}
            >
              {/* Phase Header */}
              <button 
                onClick={() => togglePhase(phase.id)}
                className={`w-full p-4 flex items-center gap-4 text-left transition hover:bg-slate-50
                  ${isExpanded ? 'bg-slate-50' : ''}
                `}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className={`font-bold text-lg truncate ${isActive ? 'text-indigo-700' : 'text-slate-800'}`}>
                      {phase.name}
                    </h3>
                    {isActive && <span className="bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><PlayCircle size={10} /> CURRENT</span>}
                    {isCatchUp && !isComplete && <span className="bg-orange-100 text-orange-700 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle size={10} /> CATCH UP</span>}
                    {isComplete && <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><Check size={10} /> DONE</span>}
                  </div>
                  <div className="flex items-center text-xs text-slate-500 gap-3">
                    <span className="flex items-center gap-1"><Clock size={12} /> {phase.timeframe}</span>
                    <span className="text-slate-300">|</span>
                    <span className="font-medium text-slate-600">{progress}% Complete</span>
                  </div>
                </div>

                <div className="text-slate-400">
                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </button>
              
              {/* Progress Line for Collapsed State */}
              {!isExpanded && (
                <div className="h-1 bg-slate-50 w-full">
                  <div className={`h-full ${isComplete ? 'bg-emerald-500' : isActive ? 'bg-indigo-500' : 'bg-slate-300'}`} style={{width: `${progress}%`}}></div>
                </div>
              )}

              {/* Phase Content */}
              {isExpanded && (
                <div className="p-4 border-t border-slate-100 animate-in slide-in-from-top-2">
                  {visibleCategories.map(category => {
                    const visibleTasks = category.tasks.filter(isTaskRelevant);
                    if (visibleTasks.length === 0) return null;

                    return (
                      <div key={category.name} className="mb-6 last:mb-0">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 pl-2 border-l-2 border-indigo-200">
                          {category.name}
                        </h4>
                        
                        <div className="space-y-2">
                          {visibleTasks.map(task => {
                            const state = userTasks.find(t => t.id === task.id);
                            const isCompleted = state?.completed || false;
                            const isDetailsOpen = expandedTaskIds.includes(task.id);
                            const hasDependency = DEPENDENCIES[task.id];

                            return (
                              <div 
                                key={task.id}
                                className={`group rounded-lg border transition-all duration-300 
                                  ${isCompleted ? 'bg-slate-50/50 border-slate-100' : 'bg-white border-slate-200 hover:border-indigo-200 hover:shadow-sm'}
                                `}
                              >
                                {/* Task Row */}
                                <div 
                                  onClick={() => toggleTaskDetails(task.id)}
                                  className="p-3 flex items-start gap-3 cursor-pointer"
                                >
                                  <button
                                    onClick={(e) => toggleTaskCompletion(task.id, e)}
                                    className={`mt-0.5 flex-shrink-0 transition-transform active:scale-90 focus:outline-none 
                                      ${isCompleted ? 'text-emerald-500' : 'text-slate-300 hover:text-indigo-400'}
                                    `}
                                    aria-label={`Mark ${task.task} as ${isCompleted ? 'incomplete' : 'complete'}`}
                                  >
                                    {isCompleted ? <CheckSquare size={20} /> : <Square size={20} />}
                                  </button>

                                  <div className="flex-1">
                                    <div className="flex justify-between items-start gap-2">
                                      <span className={`text-sm font-medium transition-all duration-300 ${isCompleted ? 'text-slate-400 line-through decoration-slate-300' : 'text-slate-800'}`}>
                                        {task.task}
                                      </span>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        {task.chatMentioned && (
                                          <span title="Community Validated">
                                            <Users size={14} className="text-blue-500" />
                                          </span>
                                        )}
                                        {getPriorityBadge(task.priority)}
                                      </div>
                                    </div>
                                    
                                    {!isDetailsOpen && (
                                      <div className="mt-1">
                                         {hasDependency && (
                                           <div className="flex items-center gap-1 text-[10px] text-amber-600 font-bold mb-1">
                                             <AlertTriangle size={10} /> Dependency Check
                                           </div>
                                         )}
                                         <p className="text-xs text-slate-400 truncate max-w-[90%]">
                                          {task.details}
                                         </p>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className={`text-slate-300 transition-transform duration-300 ${isDetailsOpen ? 'rotate-180' : ''}`}>
                                     <ChevronDown size={16} />
                                  </div>
                                </div>

                                {/* Expanded Details */}
                                {isDetailsOpen && (
                                  <div className="px-3 pb-3 pt-0 ml-8 animate-in fade-in slide-in-from-top-1">
                                    {hasDependency && !isCompleted && (
                                      <div className="mb-2 bg-amber-50 p-2 rounded border border-amber-100 flex items-start gap-2">
                                        <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-xs text-amber-800 font-medium">{hasDependency}</p>
                                      </div>
                                    )}

                                    <p className="text-sm text-slate-600 leading-relaxed mb-3">
                                      {task.details}
                                    </p>
                                    
                                    {task.chatQuote && (
                                      <div className="flex gap-2 bg-indigo-50 p-2.5 rounded-lg border border-indigo-100 text-xs text-indigo-800">
                                        <MessageSquare size={14} className="flex-shrink-0 mt-0.5 text-indigo-400" />
                                        <span className="italic">"{task.chatQuote}"</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Checklist;